import { useState, useEffect } from 'react'
import { MessageCircle, Star, Save } from 'lucide-react'
import { getPublicStats, savePublicStats, productKey, getWhatsAppConfig, saveWhatsAppConfig } from '../../lib/publicData'

const PUBLIC_MENU_ITEMS = [
  { branch: 'kitchen', section: 'Desres Kitchen', items: [
    'Full Mukokoroshi','Half Mukokoroshi','Mukokoroshi Plate','Mukokoroshi (Single)',
    'Full Steak','Half Steak','Full Stew Vors','Half Stew Vors',
    'Khwandadza Khuhu x3','Khwanda Plate','Half Khwanda Dza Khuhu',
    'Full Liver','Half Liver','Liver Plate',
    'Full Mala A Khuhu','Half Mala A Khuhu','Mala Plate',
    'Mashonzha Plate','Makhaha','Mutuku',
  ]},
  { branch: 'bakery', section: 'Desres Bakery', items: [
    'Bread','Half Bread','Buns (6-pack)','Scones','Fatcook',
    'White Rolls (pack)','Yellow Rolls (pack)','Hotdog','Russian','Burger','Muffin',
  ]},
  { branch: 'yawehs', section: "Yahweh's Delight", items: [
    'Scones','Fatcook','Bread (Brown)','Bread (White)',
    'Polony & Cheese','Chicken Mayo','Bacon & Cheese','Dagwood',
    'Vienna Kota','Russian Kota','Egg Kota','Patty Kota',
  ]},
]

export default function PublicSiteTab({ addToast, department }) {
  const deptBranches = department === 'yawehs' ? ['yawehs'] : ['kitchen', 'bakery']
  const [stats, setStats] = useState({})
  const [waConfig, setWaConfig] = useState({ kitchen: '', yawehs: '' })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    Promise.all([getPublicStats(), getWhatsAppConfig()]).then(([s, w]) => {
      setStats(s)
      setWaConfig(w)
      setLoading(false)
    })
  }, [])

  const updateStat = (key, field, value) => {
    setStats(prev => ({
      ...prev,
      [key]: { ...(prev[key] || { rating: 0, reviewCount: 0, totalSold: 0 }), [field]: Number(value) || 0 }
    }))
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      await Promise.all([savePublicStats(stats), saveWhatsAppConfig(waConfig)])
      addToast('Public site data saved', 'success')
    } catch {
      addToast('Failed to save', 'error')
    }
    setSaving(false)
  }

  if (loading) return <p className="text-gray-500 text-sm text-center py-8">Loading public site data...</p>

  return (
    <div className="space-y-8">
      {/* WhatsApp Config */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <MessageCircle size={16} className="text-green-400" /> WhatsApp Order Numbers
        </h3>
        <p className="text-xs text-gray-500 mb-4">Enter full international format (e.g. 27812345678). Customers will be directed here when ordering.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {deptBranches.includes('kitchen') && (
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Desres Kitchen (Limpopo)</label>
              <input
                type="text"
                value={waConfig.kitchen}
                onChange={e => setWaConfig(p => ({ ...p, kitchen: e.target.value }))}
                placeholder="27812345678"
                className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full"
              />
            </div>
          )}
          {deptBranches.includes('yawehs') && (
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Yahweh's Delight (Pretoria)</label>
              <input
                type="text"
                value={waConfig.yawehs}
                onChange={e => setWaConfig(p => ({ ...p, yawehs: e.target.value }))}
                placeholder="27812345678"
                className="bg-gray-800 border border-gray-700 rounded px-3 py-2 text-sm text-white w-full"
              />
            </div>
          )}
        </div>
      </div>

      {/* Product Stats & Reviews */}
      <div>
        <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
          <Star size={16} className="text-amber-400" /> Product Reviews & Stats
        </h3>
        <p className="text-xs text-gray-500 mb-4">Set ratings, review counts, and total sold for products shown on the public menu.</p>

        {PUBLIC_MENU_ITEMS.filter(group => deptBranches.includes(group.branch)).map(group => (
          <div key={group.branch} className="mb-6">
            <h4 className="text-xs font-medium text-brand-400 uppercase tracking-wider mb-3">{group.section}</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-400 text-xs border-b border-gray-800">
                    <th className="text-left py-2 px-2">Product</th>
                    <th className="text-center py-2 px-2 w-20">Rating</th>
                    <th className="text-center py-2 px-2 w-20">Reviews</th>
                    <th className="text-center py-2 px-2 w-24">Total Sold</th>
                  </tr>
                </thead>
                <tbody>
                  {group.items.map(name => {
                    const key = productKey(group.branch, name)
                    const s = stats[key] || { rating: 0, reviewCount: 0, totalSold: 0 }
                    return (
                      <tr key={key} className="border-b border-gray-800/50">
                        <td className="py-1.5 px-2 text-gray-300">{name}</td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            min="0" max="5" step="0.1"
                            value={s.rating || ''}
                            onChange={e => updateStat(key, 'rating', e.target.value)}
                            placeholder="0"
                            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-16 text-center"
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            min="0"
                            value={s.reviewCount || ''}
                            onChange={e => updateStat(key, 'reviewCount', e.target.value)}
                            placeholder="0"
                            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-16 text-center"
                          />
                        </td>
                        <td className="py-1.5 px-2">
                          <input
                            type="number"
                            min="0"
                            value={s.totalSold || ''}
                            onChange={e => updateStat(key, 'totalSold', e.target.value)}
                            placeholder="0"
                            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white w-20 text-center"
                          />
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {/* Save button */}
      <div className="sticky bottom-0 bg-gray-950/90 backdrop-blur-sm border-t border-gray-800 py-4 -mx-4 px-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-6 py-2.5 rounded-lg text-sm font-semibold"
        >
          <Save size={14} />
          {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  )
}
